import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const LiveMarkets = () => {
  const [cryptoData, setCryptoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isLive, setIsLive] = useState(true);

  // Forex data (simulated as no free API available without key)
  const [forexData] = useState([
    { pair: 'EUR/USD', rate: 1.0892, change: 0.15, flag1: '🇪🇺', flag2: '🇺🇸' },
    { pair: 'GBP/USD', rate: 1.2734, change: -0.08, flag1: '🇬🇧', flag2: '🇺🇸' },
    { pair: 'USD/JPY', rate: 154.32, change: 0.23, flag1: '🇺🇸', flag2: '🇯🇵' },
    { pair: 'USD/CHF', rate: 0.8845, change: -0.12, flag1: '🇺🇸', flag2: '🇨🇭' },
    { pair: 'AUD/USD', rate: 0.6589, change: 0.34, flag1: '🇦🇺', flag2: '🇺🇸' },
    { pair: 'USD/CAD', rate: 1.3654, change: -0.05, flag1: '🇺🇸', flag2: '🇨🇦' }
  ]);

  const fetchCryptoData = async () => {
    try {
      const response = await fetch(
        'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=6&page=1&sparkline=false&price_change_percentage=24h'
      );
      
      if (!response.ok) {
        throw new Error('API rate limit reached');
      }
      
      const data = await response.json();
      setCryptoData(data);
      setLastUpdate(new Date());
      setError(null);
      setIsLive(true);
    } catch (err) {
      console.error('Error fetching crypto data:', err);
      setError(err.message);
      setIsLive(false);
      // Use fallback data if API fails
      if (cryptoData.length === 0) {
        setCryptoData([
          { id: 'bitcoin', symbol: 'btc', name: 'Bitcoin', image: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png', current_price: 67234.52, price_change_percentage_24h: 2.34, market_cap: 1320000000000, total_volume: 28500000000 },
          { id: 'ethereum', symbol: 'eth', name: 'Ethereum', image: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png', current_price: 3456.78, price_change_percentage_24h: 1.87, market_cap: 415600000000, total_volume: 15200000000 },
          { id: 'binancecoin', symbol: 'bnb', name: 'BNB', image: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png', current_price: 598.34, price_change_percentage_24h: -0.54, market_cap: 89200000000, total_volume: 1800000000 },
          { id: 'solana', symbol: 'sol', name: 'Solana', image: 'https://assets.coingecko.com/coins/images/4128/small/solana.png', current_price: 178.92, price_change_percentage_24h: 5.23, market_cap: 82100000000, total_volume: 3400000000 },
          { id: 'ripple', symbol: 'xrp', name: 'XRP', image: 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png', current_price: 0.5234, price_change_percentage_24h: -1.12, market_cap: 28900000000, total_volume: 1200000000 },
          { id: 'cardano', symbol: 'ada', name: 'Cardano', image: 'https://assets.coingecko.com/coins/images/975/small/cardano.png', current_price: 0.4567, price_change_percentage_24h: 3.45, market_cap: 16100000000, total_volume: 890000000 }
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCryptoData();
    
    // Refresh every 60 seconds (CoinGecko free tier limit)
    const interval = setInterval(fetchCryptoData, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price) => {
    if (price >= 1000) return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (price >= 1) return `$${price.toFixed(2)}`;
    return `$${price.toFixed(4)}`;
  };

  const formatVolume = (volume) => {
    if (volume >= 1e12) return `$${(volume / 1e12).toFixed(2)}T`;
    if (volume >= 1e9) return `$${(volume / 1e9).toFixed(2)}B`;
    if (volume >= 1e6) return `$${(volume / 1e6).toFixed(2)}M`;
    return `$${volume.toLocaleString()}`;
  };

  return (
    <section id="markets" className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
            <span className="text-cyan-400 text-sm font-medium">Live Market Data</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Real-Time Market Rates
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Live prices from global exchanges powered by CoinGecko API.
          </p>
          <div className="flex items-center justify-center mt-4 space-x-4">
            <div className={`flex items-center text-sm ${isLive ? 'text-emerald-400' : 'text-yellow-400'}`}>
              {isLive ? <Wifi className="w-4 h-4 mr-2" /> : <WifiOff className="w-4 h-4 mr-2" />}
              {isLive ? 'Live Data' : 'Cached Data'}
            </div>
            <div className="flex items-center text-sm text-gray-500">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Last updated: {lastUpdate.toLocaleTimeString()}
            </div>
          </div>
        </div>

        <Tabs defaultValue="crypto" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8 bg-slate-800 p-1 rounded-xl">
            <TabsTrigger 
              value="crypto" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-600 data-[state=active]:text-white rounded-lg"
            >
              Cryptocurrency
            </TabsTrigger>
            <TabsTrigger 
              value="forex"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-600 data-[state=active]:text-white rounded-lg"
            >
              Forex
            </TabsTrigger>
          </TabsList>

          <TabsContent value="crypto">
            {loading && cryptoData.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 animate-pulse">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-slate-700 rounded-full" />
                      <div className="space-y-2">
                        <div className="h-4 w-20 bg-slate-700 rounded" />
                        <div className="h-3 w-12 bg-slate-700 rounded" />
                      </div>
                    </div>
                    <div className="h-8 w-32 bg-slate-700 rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cryptoData.map((coin) => (
                  <div
                    key={coin.id}
                    className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all duration-300 group"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                          <img
                            src={coin.image}
                            alt={coin.name}
                            className="w-10 h-10 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<span class="text-lg font-bold text-cyan-400">${coin.symbol.charAt(0).toUpperCase()}</span>`;
                            }}
                          />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{coin.name}</h3>
                          <p className="text-gray-500 text-sm uppercase">{coin.symbol}</p>
                        </div>
                      </div>
                      <div
                        className={`flex items-center px-2 py-1 rounded-full text-sm font-medium ${
                          coin.price_change_percentage_24h >= 0
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : 'bg-red-500/10 text-red-400'
                        }`}
                      >
                        {coin.price_change_percentage_24h >= 0 ? (
                          <TrendingUp className="w-3 h-3 mr-1" />
                        ) : (
                          <TrendingDown className="w-3 h-3 mr-1" />
                        )}
                        {Math.abs(coin.price_change_percentage_24h).toFixed(2)}%
                      </div>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-2xl font-bold text-white">
                          {formatPrice(coin.current_price)}
                        </p>
                        <p className="text-gray-500 text-sm">MCap: {formatVolume(coin.market_cap)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-500 text-sm">Vol 24h</p>
                        <p className="text-gray-300 font-medium">{formatVolume(coin.total_volume)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="forex">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {forexData.map((pair, index) => (
                <div
                  key={index}
                  className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex -space-x-2">
                        <span className="text-2xl">{pair.flag1}</span>
                        <span className="text-2xl">{pair.flag2}</span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{pair.pair}</h3>
                        <p className="text-gray-500 text-sm">Forex Pair</p>
                      </div>
                    </div>
                    <div
                      className={`flex items-center px-2 py-1 rounded-full text-sm font-medium ${
                        pair.change >= 0
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-red-500/10 text-red-400'
                      }`}
                    >
                      {pair.change >= 0 ? (
                        <TrendingUp className="w-3 h-3 mr-1" />
                      ) : (
                        <TrendingDown className="w-3 h-3 mr-1" />
                      )}
                      {Math.abs(pair.change).toFixed(2)}%
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-white">
                    {pair.rate.toFixed(4)}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-500 text-sm mt-6">
              * Forex rates are indicative. Real-time forex data requires a premium API subscription.
            </p>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default LiveMarkets;
